/**
 * Alojamento de uma rota de plugin.
 *
 * Cada rota tem a sua fronteira de erro e o seu proprio `Suspense`. Uma avaria
 * nas financas fica contida num cartao e nao chega perto do resto do aplicativo.
 */
import { Suspense, lazy, useMemo, type ComponentType } from 'react';
import { useNavigate } from 'react-router-dom';
import { Screen, Spinner } from '@flos/ui';
import type { RegisteredRoute } from '@flos/core';
import { useApp } from '../state/AppContext';
import { PluginErrorBoundary } from './PluginErrorBoundary';

export interface PluginRouteHostProps {
  route: RegisteredRoute;
}

export function PluginRouteHost({ route }: PluginRouteHostProps) {
  const { boot, manifests } = useApp();
  const navigate = useNavigate();

  const Loaded = useMemo<ComponentType<unknown>>(() => lazy(route.component), [route.component]);
  const pluginName =
    manifests.find((manifest) => manifest.id === route.pluginId)?.name ?? route.pluginId;

  // `activate()` corre uma vez por sessao e nunca lanca para fora do registro.
  void boot.core.registry.activate(route.pluginId);

  return (
    <Screen title={route.title} back={() => navigate(-1)}>
      <PluginErrorBoundary
        pluginName={pluginName}
        onError={(error) =>
          boot.core.logger.error('Tela de plugin rebentou', {
            plugin: route.pluginId,
            rota: route.fullPath,
            erro: error.message,
          })
        }
      >
        <Suspense fallback={<Spinner label="Abrindo…" />}>
          <Loaded />
        </Suspense>
      </PluginErrorBoundary>
    </Screen>
  );
}
